import { useTranslation } from 'react-i18next'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterConfig {
  key: string
  label: string
  type: 'select'
  options: FilterOption[]
}

interface SearchAndFilterBarProps {
  searchTerm: string
  onSearchChange: (term: string) => void
  searchPlaceholder?: string
  filters?: FilterConfig[]
  filterValues?: Record<string, any>
  onFilterChange?: (key: string, value: any) => void
}

/**
 * Reusable Search and Filter Bar Component
 * Dark mode geek aesthetic
 */
export default function SearchAndFilterBar({
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Rechercher...',
  filters = [],
  filterValues = {},
  onFilterChange,
}: SearchAndFilterBarProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
      <div className="flex gap-4 flex-wrap items-end">
        {/* Search Input */}
        <div className="flex-1 min-w-64">
          <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
            {t('common.search')}
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 font-mono text-sm focus:outline-none focus:border-cyan-500 transition"
            />
          </div>
        </div>

        {/* Filters */}
        {filters.map((filter) => (
          <div key={filter.key} className="min-w-48">
            <label className="block text-xs font-mono font-bold text-purple-400 mb-2 uppercase tracking-wider">
              {filter.label}
            </label>
            <select
              value={filterValues[filter.key] || ''}
              onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 font-mono text-sm focus:outline-none focus:border-purple-500 transition"
            >
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  )
}
